import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ExternalLink, Pencil, Plus, Printer, X } from "lucide-react";
import { useEffect, useId, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import {
  archiveSetupItem,
  formPayload,
  issueAssetQr,
  listSetupItems,
  saveSetupItem,
  type QrIssueResponse,
} from "./setup-api.js";
import {
  itemSummary,
  itemTitle,
  labelFor,
  type SetupField,
  type SetupItem,
  type SetupResource,
} from "./resources.js";

function fieldValue(
  item: SetupItem | undefined,
  field: SetupField,
): boolean | number | string {
  const value = item?.[field.key] ?? field.defaultValue ?? "";
  if (["string", "number", "boolean"].includes(typeof value)) {
    return value as boolean | number | string;
  }
  if (field.key === "goods_summary" && Array.isArray(item?.locations)) {
    const first = item.locations[0];
    if (
      typeof first === "object" &&
      first !== null &&
      "goods_summary" in first
    ) {
      return typeof first.goods_summary === "string" ? first.goods_summary : "";
    }
  }
  return "";
}

function FieldControl({
  field,
  item,
}: {
  field: SetupField;
  item?: SetupItem;
}) {
  const id = useId();
  const value = fieldValue(item, field);

  if (field.type === "checkbox") {
    return (
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-stone-200 bg-stone-50 p-3.5">
        <input
          className="mt-0.5 size-4 accent-emerald-800"
          defaultChecked={Boolean(value)}
          name={field.key}
          type="checkbox"
        />
        <span>
          <span className="block text-sm font-bold text-stone-900">
            {field.label}
          </span>
          {field.help ? (
            <span className="mt-1 block text-xs leading-5 text-stone-500">
              {field.help}
            </span>
          ) : null}
        </span>
      </label>
    );
  }

  return (
    <div className={field.type === "textarea" ? "sm:col-span-2" : ""}>
      <Label htmlFor={id}>{field.label}</Label>
      {field.type === "select" ? (
        <select
          className="mt-1.5 flex h-11 w-full rounded-xl border border-stone-300 bg-surface px-3.5 text-sm shadow-xs outline-none focus:border-emerald-700 focus:ring-3 focus:ring-emerald-700/12"
          defaultValue={String(value)}
          id={id}
          name={field.key}
          required={field.required}
        >
          {!field.required || !value ? (
            <option value="">Not specified</option>
          ) : null}
          {field.options?.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </select>
      ) : field.type === "textarea" ? (
        <textarea
          className="mt-1.5 min-h-24 w-full resize-y rounded-xl border border-stone-300 bg-surface px-3.5 py-3 text-sm shadow-xs outline-none placeholder:text-stone-400 focus:border-emerald-700 focus:ring-3 focus:ring-emerald-700/12"
          defaultValue={String(value)}
          id={id}
          name={field.key}
          placeholder={field.placeholder}
          required={field.required}
        />
      ) : (
        <Input
          className="mt-1.5"
          defaultValue={String(value)}
          id={id}
          name={field.key}
          placeholder={field.placeholder}
          required={field.required}
          step={field.type === "number" ? "any" : undefined}
          type={field.type ?? "text"}
        />
      )}
      {field.help ? (
        <p className="mt-1.5 text-xs leading-5 text-stone-500">{field.help}</p>
      ) : null}
    </div>
  );
}

function Editor({
  fields,
  item,
  onClose,
  onSubmit,
  resource,
  saving,
  error,
}: {
  error: unknown;
  fields: readonly SetupField[];
  item?: SetupItem;
  onClose: () => void;
  onSubmit: (form: FormData) => void;
  resource: SetupResource;
  saving: boolean;
}) {
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    globalThis.addEventListener("keydown", listener);
    return () => globalThis.removeEventListener("keydown", listener);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-stone-950/45 p-3 backdrop-blur-sm sm:p-6"
      role="presentation"
    >
      <div
        aria-labelledby="setup-editor-title"
        aria-modal="true"
        className="mx-auto my-3 w-full max-w-2xl rounded-3xl border border-white/50 bg-surface shadow-2xl sm:my-8"
        role="dialog"
      >
        <div className="flex items-start justify-between border-b border-stone-200 px-5 py-5 sm:px-7">
          <div>
            <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
              {item ? "Update setup" : "New setup item"}
            </p>
            <h2
              className="mt-1 text-xl font-black tracking-tight"
              id="setup-editor-title"
            >
              {item ? `Edit ${resource.noun}` : resource.addLabel}
            </h2>
          </div>
          <Button
            aria-label="Close editor"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </div>
        <form
          className="p-5 sm:p-7"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            onSubmit(new FormData(event.currentTarget));
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((field) => (
              <FieldControl
                field={field}
                key={field.key}
                {...(item === undefined ? {} : { item })}
              />
            ))}
          </div>
          {error ? (
            <p
              className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"
              role="alert"
            >
              {displayError(error)}
            </p>
          ) : null}
          <div className="mt-7 flex flex-wrap justify-end gap-2">
            <Button disabled={saving} onClick={onClose} variant="ghost">
              Cancel
            </Button>
            <Button disabled={saving} type="submit">
              {saving ? "Saving…" : item ? "Save changes" : resource.addLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ResourcePanel({
  areaOptions = [],
  canManage,
  locationId,
  resource,
}: {
  areaOptions?: readonly { label: string; value: string }[];
  canManage: boolean;
  locationId: string;
  resource: SetupResource;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SetupItem | "new" | null>(null);
  const [qr, setQr] = useState<Record<string, QrIssueResponse>>({});
  const queryKey = ["setup", resource.key, locationId];
  const query = useQuery({
    queryFn: () => listSetupItems(resource, locationId),
    queryKey,
  });
  const save = useMutation({
    mutationFn: ({ form, item }: { form: FormData; item?: SetupItem }) =>
      saveSetupItem(
        resource,
        locationId,
        formPayload(resource.fields, form, item),
        item,
      ),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const archive = useMutation({
    mutationFn: (item: SetupItem) => archiveSetupItem(resource, item),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey }),
  });
  const qrMutation = useMutation({
    mutationFn: async (item: SetupItem) => ({
      item,
      result: await issueAssetQr(item.id),
    }),
    onSuccess: ({ item, result }) =>
      setQr((current) => ({ ...current, [item.id]: result })),
  });
  const fields = resource.fields.map((field) =>
    field.key === "area_id" ? { ...field, options: areaOptions } : field,
  );

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle>{resource.title}</CardTitle>
          <p className="mt-1 max-w-2xl text-sm leading-5 text-stone-500">
            {resource.description}
          </p>
        </div>
        {canManage ? (
          <Button
            className="shrink-0"
            onClick={() => setEditing("new")}
            size="sm"
          >
            <Plus className="size-4" />{" "}
            <span className="hidden sm:inline">{resource.addLabel}</span>
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="pt-3">
        {query.isPending ? (
          <LoadingState label={`Loading ${resource.title.toLowerCase()}`} />
        ) : null}
        {query.error ? (
          <ErrorState
            message={displayError(query.error)}
            onAction={() => void query.refetch()}
            title={`${resource.title} aren’t available`}
          />
        ) : null}
        {query.data?.length === 0 ? (
          <EmptyState
            calm
            description={resource.description}
            title={`No ${resource.noun}s yet`}
          />
        ) : null}
        {query.data && query.data.length > 0 ? (
          <div className="divide-y divide-stone-100 rounded-xl border border-stone-200">
            {query.data.map((item) => (
              <div
                className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center"
                key={item.id}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-bold text-stone-950">
                      {itemTitle(resource, item)}
                    </p>
                    {typeof item.status === "string" ? (
                      <Badge
                        tone={
                          item.status === "active" || item.status === "ok"
                            ? "positive"
                            : item.status === "unsafe"
                              ? "danger"
                              : "neutral"
                        }
                      >
                        {labelFor(item.status)}
                      </Badge>
                    ) : null}
                    {typeof item.proof_status === "string" ? (
                      <Badge
                        tone={
                          item.proof_status === "proven"
                            ? "positive"
                            : "attention"
                        }
                      >
                        {labelFor(item.proof_status)}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="mt-1 truncate text-xs text-stone-500">
                    {itemSummary(resource, item)}
                  </p>
                  {qr[item.id] ? (
                    <a
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 hover:text-emerald-950"
                      href={qr[item.id]!.printable_label.label_url}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Open printable QR label{" "}
                      <ExternalLink className="size-3" />
                    </a>
                  ) : null}
                </div>
                {canManage ? (
                  <div className="flex shrink-0 flex-wrap gap-1">
                    {resource.key === "assets" ? (
                      <Button
                        disabled={qrMutation.isPending}
                        onClick={() => qrMutation.mutate(item)}
                        size="sm"
                        variant="ghost"
                      >
                        <Printer className="size-3.5" /> QR label
                      </Button>
                    ) : null}
                    <Button
                      onClick={() => setEditing(item)}
                      size="sm"
                      variant="ghost"
                    >
                      <Pencil className="size-3.5" /> Edit
                    </Button>
                    <Button
                      disabled={archive.isPending}
                      onClick={() => {
                        if (
                          globalThis.confirm(
                            `Are you sure you want to ${resource.archiveLabel?.toLowerCase() ?? "archive"} this ${resource.noun}?`,
                          )
                        )
                          archive.mutate(item);
                      }}
                      size="sm"
                      variant="ghost"
                    >
                      <Archive className="size-3.5" />{" "}
                      {resource.archiveLabel ?? "Archive"}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {archive.error || qrMutation.error ? (
          <p className="mt-3 text-sm font-semibold text-red-800" role="alert">
            {displayError(archive.error ?? qrMutation.error)}
          </p>
        ) : null}
      </CardContent>
      {editing ? (
        <Editor
          error={save.error}
          fields={fields}
          {...(editing === "new" ? {} : { item: editing })}
          onClose={() => setEditing(null)}
          onSubmit={(form) =>
            save.mutate({
              form,
              ...(editing === "new" ? {} : { item: editing }),
            })
          }
          resource={resource}
          saving={save.isPending}
        />
      ) : null}
    </Card>
  );
}
